import { useState, useCallback, useEffect, useRef } from "react";
import cadviewer from "cadviewer";
import { getSelectedSpaces } from "../utils/spaceManagement";
import { SearchMode } from "../types/drawerTypes";
import EventSystem from "../../../../../events/EventSystem";

export const useSpaceSelection = (
  multiSelectArray: string[],
  searchMode: SearchMode,
  setSearchMode: (mode: SearchMode) => void,
  setMultiSelectArray: (spaces: string[]) => void,
  /** Identity of the loaded plan; the saved selection is cleared only when this
   *  changes (a real plan switch) — not on every drawing load-end, which also
   *  fires on Quick PDF publish / re-renders and would wipe the selection. */
  planKey?: string
) => {
  const [selectedSpacesChips, setSelectedSpacesChips] = useState<string[]>([]);

  const handleRemoveSpaceChip = useCallback((spaceToRemove: string) => {
    // Turn off highlight in CADViewer for the deselected space
    cadviewer.cvjs_removeMultiSelectItem(spaceToRemove);
    
    setSelectedSpacesChips((prev) =>
      prev.filter((space) => space !== spaceToRemove)
    );
  }, []);

  const handleClearAllSpaceChips = useCallback(() => {
    setSelectedSpacesChips([]);
    // clear space on canvas
    cadviewer.cvjs_clearSpaceLayer();
    cadviewer.cvjs_clearMultiSelectArrays(); // clear CADViewer multiselect
    setMultiSelectArray([]); // clear context
  }, [setMultiSelectArray]);

  useEffect(() => {
    const handleAutoAddSpaces = (data: any) => {
      const spaces = data as string[];
      const uniqueSpaces = getSelectedSpaces(spaces);

      // Never let an empty auto-add wipe an existing selection — stray clicks
      // fire the drag-rect handler with no spaces. "Clear Spaces" is the
      // explicit way to empty the list.
      if (uniqueSpaces.length === 0) return;

      setSelectedSpacesChips(uniqueSpaces);

      // Auto-switch to "current" mode if in "global" mode
      if (searchMode === "global") {
        setSearchMode("current");
      }
    };

    EventSystem.subscribe("cadviewer.auto_add_spaces", handleAutoAddSpaces);

    return () => {
      EventSystem.unsubscribe("cadviewer.auto_add_spaces", handleAutoAddSpaces);
    };
  }, [searchMode, setSearchMode]);

  // Clear the saved selection only when the plan actually changes (a real plan
  // switch). Previously this was tied to `cvjs_onLoadEnd`, which also fires on
  // Quick PDF publish / re-renders and wiped the selection mid-report.
  const prevPlanKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevPlanKeyRef.current === undefined) {
      // First observed plan — nothing selected yet, just record it.
      prevPlanKeyRef.current = planKey;
      return;
    }
    if (prevPlanKeyRef.current !== planKey) {
      prevPlanKeyRef.current = planKey;
      handleClearAllSpaceChips();
    }
  }, [planKey, handleClearAllSpaceChips]);

  const handleAddSelectedSpaces = useCallback(() => {
    const uniqueSpaces = getSelectedSpaces(multiSelectArray);

    if (uniqueSpaces.length === 0) {
      console.log("No spaces selected");
      return;
    }

    // REPLACE chips with currently selected spaces (not accumulate)
    setSelectedSpacesChips(uniqueSpaces);

    // Auto-switch to "current" mode if in "global" mode
    if (searchMode === "global") {
      setSearchMode("current");
    }
  }, [multiSelectArray, searchMode, setSearchMode]);

  return {
    selectedSpacesChips,
    setSelectedSpacesChips,
    handleAddSelectedSpaces,
    handleRemoveSpaceChip,
    handleClearAllSpaceChips,
  };
};
